import { compareAsc, parseISO } from "date-fns";

import type { SortOption } from "@/components/sortDropdown";
import { getUserDisplayName } from "@/helpers/userDisplay";
import type { FolderItem, Item } from "@/types/fse";
import { formatFolderContentsCount } from "@/views/drive/components/driverRowName/folderCounts";
import { DRIVE_SHARING_NAME_THRESHOLD } from "./constants";
import type { DirectoryUsersById, DriveAccessLabel, DriveScope } from "./types";

const isFolder = (item: Item | undefined): item is FolderItem => item?.type === "folder";

export const extractRootItems = (root?: Item): Item[] => (isFolder(root) ? Object.values(root.items) : []);

export const mergeRootItems = (ownedRoot?: Item, sharedRoot?: Item): Item[] => {
  const byId = new Map<string, Item>();
  [...extractRootItems(ownedRoot), ...extractRootItems(sharedRoot)].forEach(item => {
    if (!byId.has(item.id)) byId.set(item.id, item);
  });
  return [...byId.values()];
};

export const filterOwnedSearchResults = (items: Item[], authenticatedUserId: string): Item[] =>
  items.filter(item => item.owner_id === authenticatedUserId);

export const filterItemsForScope = (
  items: Item[],
  scope: DriveScope,
  authenticatedUserId: string,
  sharedIds: ReadonlySet<string>,
): Item[] => {
  if (scope === "owned") return filterOwnedSearchResults(items, authenticatedUserId);
  if (scope === "shared") return items.filter(item => sharedIds.has(item.id));
  return items;
};

export const sortDriveItems = (items: Item[], sort: SortOption): Item[] => {
  const compare = (left: Item, right: Item): number => {
    if (sort.field === "name") {
      const result = left.name.localeCompare(right.name, undefined, { sensitivity: "base" });
      return sort.direction === "asc" ? result : -result;
    }
    const result = compareAsc(parseISO(left.created_at), parseISO(right.created_at));
    return sort.direction === "asc" ? result : -result;
  };
  return [
    ...items.filter(item => item.type === "folder").sort(compare),
    ...items.filter(item => item.type === "file").sort(compare),
  ];
};

export const paginateDriveItems = (items: Item[], page: number, pageSize: number): Item[] =>
  items.slice((page - 1) * pageSize, page * pageSize);

export const getDriveAccessLabel = (item: Item, authenticatedUserId: string): DriveAccessLabel =>
  item.owner_id === authenticatedUserId ? "Owner" : "Viewer";

export const getItemMetadata = (item: Item): string => {
  if (item.type === "folder") return formatFolderContentsCount(item) ?? "Empty";
  const extension = item.name.includes(".") ? item.name.split(".").pop()?.trim().toUpperCase() : undefined;
  return extension || "Document";
};

export const getSharingUserIds = (item: Item): string[] => [
  ...new Set([item.owner_id, ...item.permissions.map(permission => permission.user_id)].filter(Boolean)),
];

export const collectDirectoryUserIds = (items: Item[], authenticatedUserId: string): string[] => {
  const ids = new Set<string>();
  items.forEach(item => {
    ids.add(item.owner_id);
    const sharingIds = getSharingUserIds(item);
    if (sharingIds.length <= DRIVE_SHARING_NAME_THRESHOLD) {
      sharingIds.filter(id => id !== authenticatedUserId).forEach(id => ids.add(id));
    }
  });
  return [...ids].sort();
};

export const formatSharingSummary = (
  item: Item,
  authenticatedUserId: string,
  usersById: DirectoryUsersById,
): string => {
  if (item.access === "public") return "Anyone with access";
  const ids = getSharingUserIds(item);
  if (item.access === "restricted" && ids.every(id => id === authenticatedUserId)) return "Only you";
  if (ids.length > DRIVE_SHARING_NAME_THRESHOLD) return `Shared with ${ids.length} people`;
  const names = ids.filter(id => id !== authenticatedUserId).map(id => getUserDisplayName(usersById.get(id)));
  return names.length
    ? `Shared with ${[...names, "you"].join(names.length > 1 ? ", " : " and ").replace(/, ([^,]+)$/, " and $1")}`
    : "Only you";
};
